import React from 'react';
import { MessageCircle, Mail } from 'lucide-react';
import { SmallText, BodyText, Heading } from '../../../components/brand/BrandTypography';
import { TertiaryButton } from '../../../components/brand/BrandButton';

const SupportTab = ({ 
    supportTickets, 
    contactSubmissions, 
    supportLoading, 
    onFetch, 
    onSelectTicket,
    onUpdateContactStatus 
}) => {
    return (
        <div className="space-y-6">
            <div className="mb-4 p-4 bg-[#FFBB98]/30 rounded-xl border border-[#FFBB98]/20">
                <SmallText className="text-[#FFBB98]">
                    <strong>Support Management</strong> - View and respond to support tickets and contact form submissions.
                </SmallText>
            </div>
            
            {supportLoading ? (
                <div className="text-center py-12">
                    <div className="w-3 h-3 rounded-full bg-[#FFBB98] animate-pulse mx-auto mb-4" />
                    <SmallText className="text-[#7D8E95]">Loading support data...</SmallText>
                </div>
            ) : (
                <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-xl border border-[#FFBB98] p-4 text-center">
                            <div className="text-2xl font-bold text-[#FFBB98]">{supportTickets.stats?.open || 0}</div>
                            <SmallText className="text-[#7D8E95]">Open Tickets</SmallText>
                        </div>
                        <div className="bg-white rounded-xl border border-[#FFBB98] p-4 text-center">
                            <div className="text-2xl font-bold text-[#7D8E95]">{supportTickets.stats?.in_progress || 0}</div>
                            <SmallText className="text-[#7D8E95]">In Progress</SmallText>
                        </div>
                        <div className="bg-white rounded-xl border border-[#FFBB98] p-4 text-center">
                            <div className="text-2xl font-bold text-[#1D6B6B]">{supportTickets.stats?.resolved || 0}</div>
                            <SmallText className="text-[#7D8E95]">Resolved</SmallText>
                        </div>
                        <div className="bg-white rounded-xl border border-[#FFBB98] p-4 text-center">
                            <div className="text-2xl font-bold text-[#7D8E95]">{supportTickets.stats?.total || 0}</div>
                            <SmallText className="text-[#7D8E95]">Total</SmallText>
                        </div>
                    </div>
                    
                    {/* Support Tickets Section */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <Heading as="h3" size="text-lg">Support Tickets</Heading>
                            <TertiaryButton onClick={onFetch} size="sm">
                                Refresh
                            </TertiaryButton>
                        </div>
                        
                        {supportTickets.tickets?.length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-xl border border-[#FFBB98]">
                                <MessageCircle className="w-8 h-8 text-[#7D8E95] mx-auto mb-2" />
                                <BodyText className="text-[#7D8E95]">No support tickets yet</BodyText>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {supportTickets.tickets?.map(ticket => (
                                    <div 
                                        key={ticket.ticket_id} 
                                        className="bg-white rounded-xl border border-[#FFBB98] p-4 cursor-pointer hover:border-[#FFBB98]/50 transition-colors"
                                        onClick={() => onSelectTicket(ticket)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs text-[#7D8E95]">#{ticket.ticket_id}</span>
                                                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                                                        ticket.status === 'open' ? 'bg-[#FFBB98] text-[#FFBB98]' :
                                                        ticket.status === 'in_progress' ? 'bg-[#7D8E95] text-[#7D8E95]' :
                                                        ticket.status === 'resolved' ? 'bg-[#7D8E95]/30 text-[#1D6B6B]' :
                                                        'bg-[#FFBB98] text-[#7D8E95]'
                                                    }`}>
                                                        {ticket.status?.replace('_', ' ')}
                                                    </span>
                                                    <span className="px-2 py-0.5 text-xs rounded-full bg-[#FFBB98] text-[#7D8E95]">
                                                        {ticket.category}
                                                    </span>
                                                </div>
                                                <BodyText className="font-medium text-[#1D6B6B] truncate">{ticket.subject}</BodyText>
                                                <SmallText className="text-[#7D8E95]">
                                                    {ticket.user_email} • {ticket.messages?.length || 0} messages
                                                </SmallText>
                                            </div>
                                            <SmallText className="text-[#7D8E95] flex-shrink-0">
                                                {new Date(ticket.updated_at).toLocaleDateString()}
                                            </SmallText>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Contact Submissions Section */}
                    <div>
                        <Heading as="h3" size="text-lg" className="mb-4">Contact Form Submissions</Heading>
                        
                        {contactSubmissions.length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-xl border border-[#FFBB98]">
                                <Mail className="w-8 h-8 text-[#7D8E95] mx-auto mb-2" />
                                <BodyText className="text-[#7D8E95]">No contact submissions yet</BodyText>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {contactSubmissions.map(contact => (
                                    <div key={contact.submission_id} className="bg-white rounded-xl border border-[#FFBB98] p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs text-[#7D8E95]">#{contact.submission_id}</span>
                                                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                                                        contact.status === 'new' ? 'bg-[#FFBB98] text-[#FFBB98]' :
                                                        contact.status === 'read' ? 'bg-[#7D8E95] text-[#7D8E95]' :
                                                        'bg-[#7D8E95]/30 text-[#1D6B6B]'
                                                    }`}>
                                                        {contact.status}
                                                    </span>
                                                </div>
                                                <BodyText className="font-medium text-[#1D6B6B]">{contact.subject}</BodyText>
                                                <SmallText className="text-[#7D8E95]">{contact.name} ({contact.email})</SmallText>
                                            </div>
                                            <SmallText className="text-[#7D8E95]">
                                                {new Date(contact.created_at).toLocaleDateString()}
                                            </SmallText>
                                        </div>
                                        <p className="text-sm text-[#7D8E95] mt-2 whitespace-pre-wrap">{contact.message}</p>
                                        <div className="flex gap-2 mt-3">
                                            {contact.status === 'new' && (
                                                <TertiaryButton size="sm" onClick={() => onUpdateContactStatus(contact.submission_id, 'read')}>
                                                    Mark Read
                                                </TertiaryButton>
                                            )}
                                            {contact.status !== 'responded' && (
                                                <TertiaryButton size="sm" onClick={() => onUpdateContactStatus(contact.submission_id, 'responded')}>
                                                    Mark Responded
                                                </TertiaryButton>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default SupportTab;
